import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { formatDayLongTime, setWebTitle, stripHtml } from '@/lib/format-utils';
import { useTickets, useUsers } from '@/hooks/api-hooks';
import { SkeletonList } from '@/components/snippets';
import { useMemo, useState, useEffect } from 'react';
import { User, Ticket, TicketStatus, TICKET_STATUSES } from '@turizi/shared';
import SearchInput from '@/components/search-input';
import { filterByMultiWordSearch } from '@/utils/search-utils';
import { TICKETS_PATH, TICKET_NEW_PATH } from '@/lib/navigation';
import { localUser } from '@/services/session-service';
import { Select, Button, Tooltip } from '@mantine/core';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
    IconAlertCircle,
    IconClock,
    IconX,
    IconPlus,
    IconMessage,
    IconChevronRight,
    IconUser,
} from '@tabler/icons-react';

dayjs.extend(relativeTime);

const PRIORITY_DOTS: Record<string, string> = {
    low: 'bg-slate-300',
    medium: 'bg-blue-500',
    high: 'bg-amber-500',
    urgent: 'bg-rose-500',
};

type TicketTab = 'pending' | 'closed';

const STATUS_TABS: { key: TicketTab; labelKey: string }[] = [
    { key: 'pending', labelKey: 'status_pending' },
    { key: 'closed', labelKey: 'status_closed' },
];

const getDueInfo = (dueDatetime: any, status: string, t: (key: string) => string) => {
    if (!dueDatetime) return null;
    const due = dayjs(dueDatetime);
    const now = dayjs();

    const isClosed = status === 'closed';
    const isOverdue = due.isBefore(now) && !isClosed;
    const isToday = due.isSame(now, 'day') && !isClosed;

    if (isOverdue) {
        return {
            label: t('overdue'),
            color: 'bg-rose-50 border-rose-200 text-rose-700 font-bold',
            isOverdue: true,
            isToday: false,
        };
    }
    if (isToday) {
        return {
            label: t('today'),
            color: 'bg-amber-50 border-amber-200 text-amber-700 font-bold',
            isOverdue: false,
            isToday: true,
        };
    }
    return {
        label: formatDayLongTime(dueDatetime),
        color: 'bg-slate-50 border-slate-200 text-slate-500 font-medium',
        isOverdue: false,
        isToday: false,
    };
};

export default function TicketsPage(): JSX.Element {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const currentUser = localUser();

    const rawStatus = searchParams.get('s');
    const currentTab: TicketTab = rawStatus === 'closed' ? 'closed' : 'pending';

    useEffect(() => {
        const s = searchParams.get('s');
        if (!s || s === 'in_progress') {
            setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.set('s', 'pending');
                return next;
            }, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    const [searchTerm, setSearchTerm] = useState('');
    const [filterAssignee, setFilterAssignee] = useState<string | null>(null);
    const [filterPriority, setFilterPriority] = useState<string | null>(null);
    const [filterDueStatus, setFilterDueStatus] = useState<string | null>(null);

    const {
        data: ticketsResponse,
        isLoading,
    } = useTickets({});
    const {
        data: usersResponse,
    } = useUsers();

    const users = usersResponse?.data || [];
    const tickets = ticketsResponse?.data || [];

    setWebTitle(t('tickets'));

    const usersMap = useMemo(() => {
        return users.reduce((acc, user) => {
            acc[user.id] = user;
            return acc;
        }, {} as Record<number, User>);
    }, [users]);

    const statusCounts = useMemo(() => {
        const counts: Record<string, number> = {
            pending: 0,
            in_progress: 0,
            closed: 0,
        };
        tickets.forEach((ticket) => {
            if (counts[ticket.status] !== undefined) {
                counts[ticket.status]++;
            }
        });
        return counts;
    }, [tickets]);

    const filteredTickets = useMemo(() => {
        let result = tickets.filter((t) => {
            if (currentTab === 'pending') {
                return t.status === 'pending' || t.status === 'in_progress';
            }
            return t.status === 'closed';
        });

        if (filterAssignee) {
            if (filterAssignee === 'unassigned') {
                result = result.filter((t) => !t.assignee_id);
            } else {
                result = result.filter((t) => t.assignee_id === Number(filterAssignee));
            }
        }

        if (filterPriority) {
            result = result.filter((t) => t.priority === filterPriority);
        }

        if (filterDueStatus) {
            const now = dayjs();
            result = result.filter((t) => {
                if (!t.due_datetime) {
                    return filterDueStatus === 'no_due';
                }
                const due = dayjs(t.due_datetime);
                const isClosed = t.status === 'closed';
                const isOverdue = due.isBefore(now) && !isClosed;
                const isToday = due.isSame(now, 'day') && !isClosed;
                const isThisWeek = (due.isSame(now, 'week') || (due.isAfter(now) && due.isBefore(now.add(7, 'day')))) && !isClosed;

                if (filterDueStatus === 'overdue') return isOverdue;
                if (filterDueStatus === 'today') return isToday;
                if (filterDueStatus === 'this_week') return isThisWeek;
                return true;
            });
        }

        if (searchTerm) {
            result = filterByMultiWordSearch(result, searchTerm, (ticket) => {
                const creator = usersMap[ticket.creator_id];
                const assignee = ticket.assignee_id ? usersMap[ticket.assignee_id] : null;
                return [
                    ticket.id,
                    ticket.title,
                    stripHtml(ticket.content),
                    ticket.status,
                    ticket.priority,
                    creator?.name || creator?.email || '',
                    assignee?.name || assignee?.email || '',
                    formatDayLongTime(ticket.due_datetime),
                ].join(' ');
            });
        }

        return result;
    }, [tickets, currentTab, searchTerm, filterAssignee, filterPriority, filterDueStatus, usersMap]);

    const assigneeOptions = useMemo(() => {
        const options = [
            { value: 'unassigned', label: t('unassigned') },
        ];
        const seen = new Set<string>(['unassigned']);

        if (currentUser?.id) {
            const currentUserIdStr = String(currentUser.id);
            options.push({ value: currentUserIdStr, label: t('assigned_to_me') });
            seen.add(currentUserIdStr);
        }

        users.forEach((u) => {
            const val = String(u.id);
            if (!seen.has(val)) {
                seen.add(val);
                options.push({
                    value: val,
                    label: u.name || u.email || val,
                });
            }
        });

        return options;
    }, [users, currentUser, t]);

    const hasActiveFilters = !!(filterAssignee || filterPriority || filterDueStatus || searchTerm);

    const handleClearFilters = () => {
        setFilterAssignee(null);
        setFilterPriority(null);
        setFilterDueStatus(null);
        setSearchTerm('');
    };

    const handleSelectStatus = (tab: TicketTab) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('s', tab);
            return next;
        });
    };

    return (
        <div className="mx-4 max-w-7xl pb-10 space-y-3">
            {/* Top Bar: Status Tabs (Left) and Compact Filters + Actions (Right) in a Single Row */}
            <div className="flex items-center justify-between gap-2.5 flex-wrap">
                {/* Status Navigation Tabs */}
                <div className="flex items-center gap-1 overflow-x-auto scrollbar-none flex-shrink-0">
                    {STATUS_TABS.map((tab) => {
                        const isActive = currentTab === tab.key;
                        const count = tab.key === 'pending'
                            ? (statusCounts.pending || 0) + (statusCounts.in_progress || 0)
                            : (statusCounts.closed || 0);
                        return (
                            <button
                                key={tab.key}
                                type="button"
                                onClick={() => handleSelectStatus(tab.key)}
                                className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap ${isActive
                                    ? 'bg-slate-900 text-white shadow-2xs'
                                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                                    }`}
                            >
                                <span>{t(tab.labelKey)}</span>
                                <span
                                    className={`px-1.5 py-0.2 text-[10px] rounded-full font-bold ${isActive
                                        ? 'bg-white/20 text-white'
                                        : 'bg-slate-100 text-slate-500'
                                        }`}
                                >
                                    {count}
                                </span>
                            </button>
                        );
                    })}
                </div>

                {/* Filters, Search & New Ticket */}
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="w-36 sm:w-44 flex-shrink-0">
                        <SearchInput
                            size="xs"
                            onSearch={setSearchTerm}
                        />
                    </div>

                    <Select
                        placeholder={t('assignee')}
                        data={assigneeOptions}
                        value={filterAssignee}
                        onChange={(val) => setFilterAssignee(val)}
                        clearable
                        radius="xl"
                        size="xs"
                        className="w-28 sm:w-32 flex-shrink-0"
                    />

                    <Select
                        placeholder={t('priority')}
                        data={[
                            { value: 'low', label: t('priority_low') },
                            { value: 'medium', label: t('priority_medium') },
                            { value: 'high', label: t('priority_high') },
                            { value: 'urgent', label: t('priority_urgent') },
                        ]}
                        value={filterPriority}
                        onChange={(val) => setFilterPriority(val)}
                        clearable
                        radius="xl"
                        size="xs"
                        className="w-24 sm:w-28 flex-shrink-0"
                    />
                    {/* 
                    <Select
                        placeholder={t('due_date')}
                        data={[
                            { value: 'overdue', label: t('overdue') },
                            { value: 'today', label: t('today') },
                            { value: 'this_week', label: t('this_week') },
                            { value: 'no_due', label: t('no_due_date') },
                        ]}
                        value={filterDueStatus}
                        onChange={(val) => setFilterDueStatus(val)}
                        clearable
                        radius="xl"
                        size="xs"
                        className="w-28 sm:w-32 flex-shrink-0"
                    /> */}

                    {hasActiveFilters && (
                        <Button
                            variant="subtle"
                            color="gray"
                            radius="xl"
                            size="xs"
                            onClick={handleClearFilters}
                            className="px-2 flex-shrink-0"
                            title={t('clear_filters')}
                        >
                            <IconX size={14} />
                        </Button>
                    )}

                    <Link to={TICKET_NEW_PATH} className="flex-shrink-0">
                        <Button
                            leftSection={<IconPlus size={14} />}
                            color="black"
                            size="xs"
                            radius="xl"
                        >
                            {t('new_ticket')}
                        </Button>
                    </Link>
                </div>
            </div>


            {/* Content: List / Skeleton / Empty State */}
            {isLoading ? (
                <SkeletonList />
            ) : filteredTickets.length === 0 ? (
                <div className="py-12 text-center text-xs text-slate-400 italic">
                    <span>{t('no_tickets')}</span>
                    {hasActiveFilters && (
                        <button
                            type="button"
                            onClick={handleClearFilters}
                            className="ml-2 text-indigo-600 hover:underline not-italic font-medium cursor-pointer"
                        >
                            ({t('clear_filters')})
                        </button>
                    )}
                </div>
            ) : (
                <div className="bg-white border border-slate-200/80 rounded-xl overflow-hidden shadow-2xs divide-y divide-slate-100">
                    {filteredTickets.map((ticket) => {
                        const dueInfo = getDueInfo(ticket.due_datetime, ticket.status, t);
                        const assignee = ticket.assignee_id ? usersMap[ticket.assignee_id] : null;
                        const creator = ticket.creator_id ? usersMap[ticket.creator_id] : null;
                        const commentsCount = ticket.comments?.length || 0;
                        const isInProgress = ticket.status === 'in_progress';

                        return (
                            <Link
                                key={ticket.id}
                                to={`${TICKETS_PATH}/${ticket.id}`}
                                className={`flex items-center justify-between gap-4 px-4 py-3.5 transition-colors cursor-pointer text-inherit no-underline group ${
                                    isInProgress
                                        ? 'bg-emerald-50/70 hover:bg-emerald-100/60'
                                        : 'hover:bg-slate-50/80'
                                }`}
                            >
                                {/* Left Section: Priority Dot, Title, Content, Metadata */}
                                <div className="flex items-start gap-2.5 min-w-0 flex-1">
                                    <div className="pt-1.5 flex-shrink-0">
                                        <Tooltip label={t(`priority_${ticket.priority}`)} withArrow position="top">
                                            <span className={`block w-2 h-2 rounded-full ${PRIORITY_DOTS[ticket.priority] || 'bg-slate-300'}`} />
                                        </Tooltip>
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2">
                                            <span className="font-semibold text-sm text-slate-900">
                                                {ticket.title}
                                            </span>
                                        </div>
                                        {ticket.content && (
                                            <p className="text-xs text-slate-800 line-clamp-1 mt-0.5 font-normal">
                                                {stripHtml(ticket.content)}
                                            </p>
                                        )}
                                        <div className="flex items-center gap-3 mt-1.5 text-[11px] text-slate-400">
                                            {creator && (
                                                <span>
                                                    <span className="text-slate-400 font-medium">{creator.name || creator.email}</span>
                                                </span>
                                            )}
                                            {ticket.created_at && (
                                                <span>
                                                    {dayjs(ticket.created_at).fromNow()}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Right Section: Due Date, Comments, Assignee, Chevron */}
                                <div className="flex items-center gap-3.5 flex-shrink-0">
                                    {dueInfo && (
                                        <div className={`text-xs px-2.5 py-1 rounded border flex items-center gap-1.5 ${dueInfo.color}`}>
                                            {dueInfo.isOverdue ? <IconAlertCircle size={13} className="stroke-[2.5]" /> : <IconClock size={13} />}
                                            <span>{dueInfo.label}</span>
                                        </div>
                                    )}

                                    {commentsCount > 0 && (
                                        <div className="flex items-center gap-1 text-xs text-slate-500 bg-slate-100/80 px-2 py-0.5 rounded-full" title={`${commentsCount} ${t('comments')}`}>
                                            <IconMessage size={13} />
                                            <span>{commentsCount}</span>
                                        </div>
                                    )}

                                    <div className="flex items-center justify-end">
                                        {assignee ? (
                                            <div className="flex items-center gap-2" title={assignee.name || assignee.email}>
                                                <div className="w-6 h-6 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-700 flex items-center justify-center text-xs font-bold shadow-3xs flex-shrink-0">
                                                    {(assignee.name || assignee.email || '?').charAt(0).toUpperCase()}
                                                </div>
                                                <span className="text-xs text-slate-700 whitespace-nowrap font-medium">
                                                    {assignee.name || assignee.email}
                                                </span>
                                            </div>
                                        ) : (
                                            <span className="text-xs text-slate-400 italic">
                                                {t('unassigned')}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
